"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import {
  IoArrowBack,
  IoArrowForward,
  IoCalendarOutline,
  IoCallOutline,
  IoCheckmarkCircleOutline,
  IoLockClosedOutline,
  IoRibbonOutline,
  IoShieldCheckmarkOutline,
  IoSparkles,
  IoStar,
  IoVideocamOutline,
} from "react-icons/io5";
import ConsultationAppFlow from "../components/ConsultationAppFlow";
import { normalizeTokenFromSearch } from "../lib/consultationClient";
import { getPublicExperts } from "../lib/consultationApiClient";
import { trackEvent } from "../lib/posthog";

type Expert = {
  id: number;
  name: string;
  slug?: string | null;
  title: string | null;
  image_url: string | null;
  bio_summary: string | null;
};

type AccentTone =
  | "amber"
  | "blue"
  | "cyan"
  | "green"
  | "indigo"
  | "orange"
  | "purple"
  | "rose"
  | "sky"
  | "slate"
  | "teal";

type HeroStat = {
  icon: ReactNode;
  value: string;
  label: string;
  iconShellClass: string;
  iconClass: string;
};

type SectionIntro = {
  tag: string;
  title: string;
  description: string;
};

type HeroConfig = {
  badge: string;
  titlePrefix: string;
  titleHighlight: string;
  descriptionPrefix: string;
  descriptionHighlight: string;
  descriptionSuffix?: string;
  supportingLine?: string;
  primaryCtaText: string;
  secondaryCtaText: string;
  stats: HeroStat[];
};

type ProblemScenarioItem = {
  icon: ReactNode;
  label: string;
  problem: string;
  solution: string;
  color: AccentTone;
  ctaLabel?: string;
  href?: string;
};

type BenefitItem = {
  icon: ReactNode;
  label: string;
  problem: string;
  solution: string;
  color: AccentTone;
};

type StepItem = {
  number: string;
  title: string;
  description: string;
  icon: ReactNode;
};

type TestimonialItem = {
  name: string;
  location: string;
  problem: string;
  rating: number;
  text: string;
};

type StatItem = {
  value: string;
  label: string;
};

type WhyChooseItem = {
  icon: ReactNode;
  title: string;
  desc: string;
};

type BookingCtaConfig = {
  liveBadge: string;
  title: string;
  description: string;
  buttonText: string;
  privacyText: string;
  verifiedText: string;
};

type StickyCtaConfig = {
  title: string;
  subtitle: string;
  buttonText: string;
};

type ConsultationLandingTheme = {
  pageBackgroundClass: string;
  heroSectionClass: string;
  heroPatternClass: string;
  heroTopOrbClass: string;
  heroBottomOrbClass: string;
  heroBottomCurveFill: string;
  heroBadgeClass: string;
  heroBadgeTextClass: string;
  heroHighlightClass: string;
  heroSubtitleHighlightClass: string;
  heroPrimaryButtonClass: string;
  heroSecondaryButtonClass: string;
  expertsSectionClass: string;
  expertsBadgeClass: string;
  expertsBadgeTextClass: string;
  expertTitleClass: string;
  expertAvatarBorderClass: string;
  carouselButtonClass: string;
  reasonsBadgeClass: string;
  reasonsBadgeTextClass: string;
  benefitsBadgeClass: string;
  benefitsBadgeTextClass: string;
  processBadgeClass: string;
  processBadgeTextClass: string;
  stepCircleClass: string;
  stepNumberClass: string;
  stepBadgeClass: string;
  stepConnectorClass: string;
  stepConnectorDashClass: string;
  testimonialsBadgeClass: string;
  testimonialsBadgeTextClass: string;
  testimonialAvatarClass: string;
  testimonialStatValueClass: string;
  finalSectionClass: string;
  trustCardIconShellClass: string;
  trustCardIconClass: string;
  bookingCardClass: string;
  bookingCardHeaderClass: string;
  bookingCardBadgeClass: string;
  bookingCardBadgeTextClass: string;
  bookingCardIconShellClass: string;
  bookingCardIconClass: string;
  bookingCardPrimaryButtonClass: string;
  stickyBarClass: string;
  stickyButtonClass: string;
};

export type ConsultationModalThemeKey =
  | "generic"
  | "gold"
  | "career"
  | "love"
  | "finance"
  | "children"
  | "family"
  | "lifeDirection";

export type ConsultationLandingConfig = {
  modalTheme: ConsultationModalThemeKey;
  modalDefaultConcernArea?: string;
  theme: ConsultationLandingTheme;
  hero: HeroConfig;
  experts: SectionIntro & {
    emptyState: string;
  };
  reasons: SectionIntro & {
    items: ProblemScenarioItem[];
  };
  benefits: SectionIntro & {
    items: BenefitItem[];
  };
  process: SectionIntro & {
    items: StepItem[];
    ctaText: string;
  };
  testimonials: SectionIntro & {
    items: TestimonialItem[];
    stats: StatItem[];
  };
  whyChoose: {
    title: string;
    description: string;
    items: WhyChooseItem[];
  };
  bookingCta: BookingCtaConfig;
  stickyCta: StickyCtaConfig;
};

function cx(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(" ");
}

const problemPalette: Record<
  AccentTone,
  { panel: string; chip: string; text: string; button: string }
> = {
  amber: {
    panel: "bg-amber-50 border-amber-100",
    chip: "bg-amber-100",
    text: "text-amber-700",
    button: "hover:border-amber-200",
  },
  blue: {
    panel: "bg-blue-50 border-blue-100",
    chip: "bg-blue-100",
    text: "text-blue-700",
    button: "hover:border-blue-200",
  },
  cyan: {
    panel: "bg-cyan-50 border-cyan-100",
    chip: "bg-cyan-100",
    text: "text-cyan-700",
    button: "hover:border-cyan-200",
  },
  green: {
    panel: "bg-emerald-50 border-emerald-100",
    chip: "bg-emerald-100",
    text: "text-emerald-700",
    button: "hover:border-emerald-200",
  },
  indigo: {
    panel: "bg-indigo-50 border-indigo-100",
    chip: "bg-indigo-100",
    text: "text-indigo-700",
    button: "hover:border-indigo-200",
  },
  orange: {
    panel: "bg-orange-50 border-orange-100",
    chip: "bg-orange-100",
    text: "text-orange-700",
    button: "hover:border-orange-200",
  },
  purple: {
    panel: "bg-purple-50 border-purple-100",
    chip: "bg-purple-100",
    text: "text-purple-700",
    button: "hover:border-purple-200",
  },
  rose: {
    panel: "bg-rose-50 border-rose-100",
    chip: "bg-rose-100",
    text: "text-rose-700",
    button: "hover:border-rose-200",
  },
  sky: {
    panel: "bg-sky-50 border-sky-100",
    chip: "bg-sky-100",
    text: "text-sky-700",
    button: "hover:border-sky-200",
  },
  slate: {
    panel: "bg-slate-100 border-slate-200",
    chip: "bg-slate-200",
    text: "text-slate-700",
    button: "hover:border-slate-300",
  },
  teal: {
    panel: "bg-teal-50 border-teal-100",
    chip: "bg-teal-100",
    text: "text-teal-700",
    button: "hover:border-teal-200",
  },
};

const benefitPalette: Record<
  AccentTone,
  { bg: string; text: string; border: string; iconBg: string }
> = {
  amber: {
    bg: "bg-amber-50",
    text: "text-amber-600",
    border: "border-amber-100",
    iconBg: "bg-amber-100",
  },
  blue: {
    bg: "bg-blue-50",
    text: "text-blue-600",
    border: "border-blue-100",
    iconBg: "bg-blue-100",
  },
  cyan: {
    bg: "bg-cyan-50",
    text: "text-cyan-600",
    border: "border-cyan-100",
    iconBg: "bg-cyan-100",
  },
  green: {
    bg: "bg-emerald-50",
    text: "text-emerald-600",
    border: "border-emerald-100",
    iconBg: "bg-emerald-100",
  },
  indigo: {
    bg: "bg-indigo-50",
    text: "text-indigo-600",
    border: "border-indigo-100",
    iconBg: "bg-indigo-100",
  },
  orange: {
    bg: "bg-orange-50",
    text: "text-orange-600",
    border: "border-orange-100",
    iconBg: "bg-orange-100",
  },
  purple: {
    bg: "bg-purple-50",
    text: "text-purple-600",
    border: "border-purple-100",
    iconBg: "bg-purple-100",
  },
  rose: {
    bg: "bg-rose-50",
    text: "text-rose-600",
    border: "border-rose-100",
    iconBg: "bg-rose-100",
  },
  sky: {
    bg: "bg-sky-50",
    text: "text-sky-600",
    border: "border-sky-100",
    iconBg: "bg-sky-100",
  },
  slate: {
    bg: "bg-slate-100",
    text: "text-slate-600",
    border: "border-slate-200",
    iconBg: "bg-slate-200",
  },
  teal: {
    bg: "bg-teal-50",
    text: "text-teal-600",
    border: "border-teal-100",
    iconBg: "bg-teal-100",
  },
};

const PATTERN_CLASS =
  "bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMS41IiBmaWxsPSJ3aGl0ZSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgZmlsbD0idXJsKCNnKSIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIvPjwvc3ZnPg==')]";

const ProblemCard = ({
  icon,
  label,
  problem,
  solution,
  color,
  ctaLabel,
  href,
  onOpenQuiz,
}: ProblemScenarioItem & {
  onOpenQuiz: () => void;
}) => {
  const current = problemPalette[color];
  const actionLabel = ctaLabel || "Book this consultation";
  const actionClass = "inline-flex items-center gap-2 text-sm font-bold text-accent";

  return (
    <div
      className={`group border rounded-3xl p-6 ${current.panel} ${current.button} transition-all duration-300 hover:-translate-y-1 hover:shadow-xl`}
    >
      <div
        className={`w-14 h-14 rounded-2xl ${current.chip} ${current.text} flex items-center justify-center text-2xl mb-5`}
      >
        {icon}
      </div>
      <p className={`text-xs font-black uppercase tracking-[0.2em] ${current.text}`}>
        {label}
      </p>
      <h3 className="text-xl font-bold text-accent mt-3 mb-3 leading-snug">
        {problem}
      </h3>
      <p className="text-gray-600 text-sm leading-relaxed mb-6">{solution}</p>
      {href ? (
        <Link prefetch={false} href={href} className={actionClass}>
          {actionLabel} <IoArrowForward />
        </Link>
      ) : (
        <button onClick={onOpenQuiz} className={actionClass}>
          {actionLabel} <IoArrowForward />
        </button>
      )}
    </div>
  );
};

const ServiceCard = ({
  icon,
  problem,
  solution,
  color,
  index,
}: BenefitItem & {
  index: number;
}) => {
  const current = benefitPalette[color];

  return (
    <div
      className={`group relative ${current.bg} border ${current.border} rounded-2xl p-6 hover:shadow-lg transition-all duration-300 animate-fade-in-up`}
      style={{ animationDelay: `${index * 0.08}s` }}
    >
      <div className="flex items-center gap-4">
        <div
          className={`flex-shrink-0 w-16 h-16 ${current.iconBg} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}
        >
          <span className={`text-2xl ${current.text}`}>{icon}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-accent mt-1 mb-2 leading-snug">
            {problem}
          </h3>
          <p className="text-gray-500 text-sm leading-relaxed">{solution}</p>
        </div>
      </div>
    </div>
  );
};

const TestimonialCard = ({
  name,
  location,
  problem,
  rating,
  text,
  index,
  theme,
}: TestimonialItem & {
  index: number;
  theme: ConsultationLandingTheme;
}) => (
  <div
    className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in-up"
    style={{ animationDelay: `${index * 0.1}s` }}
  >
    <div className="flex items-center gap-0.5 mb-4">
      {[...Array(rating)].map((_, i) => (
        <IoStar key={i} className="text-yellow-400" size={16} />
      ))}
    </div>
    <p className="text-gray-700 text-sm leading-relaxed mb-6">&ldquo;{text}&rdquo;</p>
    <div className="flex items-center gap-3">
      <div
        className={cx(
          "w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm",
          theme.testimonialAvatarClass,
        )}
      >
        {name.charAt(0)}
      </div>
      <div>
        <p className="font-bold text-accent text-sm">{name}</p>
        <p className="text-[11px] text-gray-400">
          {location} | {problem}
        </p>
      </div>
      <IoShieldCheckmarkOutline
        className="text-green-500 ml-auto flex-shrink-0"
        size={20}
      />
    </div>
  </div>
);

const ExpertPanelCard = ({
  expert,
  index,
  theme,
}: {
  expert: Expert;
  index: number;
  theme: ConsultationLandingTheme;
}) => {
  const expertSlug = expert.slug || expert.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  return (
    <Link
      href={`/experts/${expertSlug}`}
      className="group block bg-white border border-gray-100 rounded-2xl p-7 sm:p-8 shadow-sm hover:shadow-xl hover:border-amber-300/80 transition-all duration-300 animate-fade-in-up cursor-pointer"
      style={{ animationDelay: `${index * 0.15}s` }}
    >
      <div className="flex items-center gap-5 sm:gap-6 mb-5 sm:mb-6">
        <div
          className={cx(
            "flex-shrink-0 w-24 h-24 sm:w-28 sm:h-28 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 border-4 overflow-hidden flex items-center justify-center shadow-md transition-transform group-hover:scale-105 duration-300",
            theme.expertAvatarBorderClass,
          )}
        >
          {expert.image_url ? (
            <img
              src={expert.image_url}
              alt={expert.name}
              className="w-full h-full object-cover object-[70%_20%]"
            />
          ) : (
            <span className="text-gray-400 text-3xl sm:text-4xl font-serif font-bold">
              {expert.name.charAt(0)}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-800 mb-1 border border-amber-200/60">
            <IoSparkles size={11} className="text-amber-600" />
            Verified Expert
          </div>
          <h3 className="text-lg sm:text-xl font-bold text-accent group-hover:text-amber-800 transition-colors truncate">
            {expert.name}
          </h3>
          <p className={cx("text-xs sm:text-sm font-semibold leading-snug mt-0.5 line-clamp-2", theme.expertTitleClass)}>
            {expert.title || "Astroji Consultation Expert"}
          </p>
        </div>
      </div>

      <p className="text-gray-600 text-xs sm:text-sm leading-relaxed line-clamp-3 mb-4">
        {expert.bio_summary ||
          "Trusted Astroji consultation expert offering thoughtful guidance rooted in practical astrology."}
      </p>

      <div className="pt-3 border-t border-gray-100 flex items-center justify-between text-xs font-semibold text-amber-700 group-hover:text-amber-800">
        <span>View Profile & Experience</span>
        <span className="transition-transform duration-200 group-hover:translate-x-1">→</span>
      </div>
    </Link>
  );
};

const BookingCTA = ({
  config,
  theme,
  onOpenQuiz,
}: {
  config: BookingCtaConfig;
  theme: ConsultationLandingTheme;
  onOpenQuiz: () => void;
}) => (
  <div className={theme.bookingCardClass}>
    <div className={cx("relative p-8 text-center overflow-hidden", theme.bookingCardHeaderClass)}>
      <div className={cx("absolute inset-0 opacity-10", PATTERN_CLASS)} />
      <div className="relative z-10">
        <div className={cx("inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4", theme.bookingCardBadgeClass)}>
          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          <span className={cx("text-xs font-bold uppercase tracking-wider", theme.bookingCardBadgeTextClass)}>
            {config.liveBadge}
          </span>
        </div>
        <h3 className="text-2xl font-bold text-white">{config.title}</h3>
      </div>
    </div>

    <div className="p-8 text-center">
      <div
        className={cx(
          "w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5",
          theme.bookingCardIconShellClass,
        )}
      >
        <IoCallOutline size={32} className={theme.bookingCardIconClass} />
      </div>
      <h3 className="text-xl font-bold text-accent mb-2">Ready for Answers?</h3>
      <p className="text-gray-500 text-sm leading-relaxed mb-8">{config.description}</p>

      <button onClick={onOpenQuiz} className={theme.bookingCardPrimaryButtonClass}>
        {config.buttonText} <IoArrowForward />
      </button>

      <div className="mt-6 flex items-center justify-center gap-6 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
        <span className="flex items-center gap-1">
          <IoLockClosedOutline /> {config.privacyText}
        </span>
        <span className="flex items-center gap-1">
          <IoShieldCheckmarkOutline /> {config.verifiedText}
        </span>
      </div>
    </div>
  </div>
);

const StepCard = ({
  number,
  title,
  description,
  icon,
  theme,
}: StepItem & {
  theme: ConsultationLandingTheme;
}) => (
  <div className="relative flex flex-col items-center text-center group">
    <div className="relative mb-6">
      <div
        className={cx(
          "w-24 h-24 rounded-full bg-white border-2 shadow-xl flex items-center justify-center transition-all duration-500 group-hover:shadow-2xl",
          theme.stepCircleClass,
        )}
      >
        <span className={cx("text-4xl font-bold bg-clip-text text-transparent", theme.stepNumberClass)}>
          {number}
        </span>
      </div>
      <div
        className={cx(
          "absolute -bottom-2 -right-2 w-10 h-10 rounded-xl flex items-center justify-center shadow-lg",
          theme.stepBadgeClass,
        )}
      >
        <span className="text-white text-lg">{icon}</span>
      </div>
    </div>
    <h3 className="text-lg font-bold text-accent mb-2">{title}</h3>
    <p className="text-gray-500 text-sm leading-relaxed max-w-[260px] mx-auto">
      {description}
    </p>
  </div>
);

const ConsultationLandingClient = ({
  config,
  supplementaryContent,
}: {
  config: ConsultationLandingConfig;
  supplementaryContent?: ReactNode;
}) => {
  const {
    theme,
    hero,
    experts: expertsSection,
    reasons,
    benefits,
    process,
    testimonials,
    whyChoose,
    bookingCta,
    stickyCta,
  } = config;
  const expertCarouselRef = useRef<SwiperType | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const token = normalizeTokenFromSearch(searchParams.get("t"), searchParams.get("id"));
  const debugStep = searchParams.get("booking_step") || searchParams.get("debug_step") || "";
  const autoStart = searchParams.get("start") === "1" || Boolean(debugStep);
  const [isFormVisible, setIsFormVisible] = useState(true);
  const [experts, setExperts] = useState<Expert[]>([]);
  const [expertsLoading, setExpertsLoading] = useState(true);
  const consultationType = config.modalTheme === "generic" ? "general" : config.modalTheme;
  const appFlowActive = Boolean(token || autoStart);
  const appFlowMode = searchParams.get("marketing") === "1" ? "marketing" : "organic";
  const landingVariantId = searchParams.get("landing_variant_id") || "";
  const benefitItems: BenefitItem[] = [
    {
      icon: <IoVideocamOutline />,
      label: "Live Video Call",
      problem: "Live 1-on-1 Video Consultation",
      solution: "All consultations are live 1-on-1 video calls with our expert Astrologers.",
      color: "slate",
    },
    ...benefits.items,
  ];



  const updateSearch = (next: URLSearchParams) => {
      router.replace(
        `${pathname}?${next.toString()}`
      );
  };

  const openQuiz = (source = "unknown") => {
    trackEvent("consultation_form_started", {
      consultation_type: consultationType,
      cta_source: source,
    });
    const next = new URLSearchParams(searchParams);
    if (!token) {
      next.set("start", "1");
    }
    updateSearch(next);
  };

  const closeQuiz = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("start");
    next.delete("t");
    next.delete("id");
    next.delete("cid");
    next.delete("rb");
    next.delete("booking_step");
    next.delete("debug_step");
    updateSearch(next);
  };

  useEffect(() => {
    let ignore = false;

    const loadExperts = async () => {
      setExpertsLoading(true);

      try {
        const data = await getPublicExperts();
        if (!ignore) {
          setExperts((data || []) as Expert[]);
          setExpertsLoading(false);
        }
      } catch (error) {
        if (!ignore) {
          console.error("Error fetching experts:", error);
          setExperts([]);
          setExpertsLoading(false);
        }
      }
    };

    void loadExperts();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFormVisible(entry.isIntersecting);
      },
      { threshold: 0.1 },
    );
    const observedNode = formRef.current;

    if (observedNode) {
      observer.observe(observedNode);
    }

    return () => {
      if (observedNode) {
        observer.unobserve(observedNode);
      }
    };
  }, []);

  if (appFlowActive) {
    return (
      <ConsultationAppFlow
        mode={appFlowMode}
        initialToken={token}
        defaultConcernArea={searchParams.get("default_concern") || config.modalDefaultConcernArea}
        landingVariantId={landingVariantId}
        debugStep={debugStep}
        onExit={closeQuiz}
      />
    );
  }

  return (
    <div className={cx("min-h-screen", theme.pageBackgroundClass)}>
      <section
        className={cx(
          "relative pt-16 pb-20 md:pt-24 md:pb-32 overflow-hidden",
          theme.heroSectionClass,
        )}
      >
        <div className={cx("absolute inset-0", PATTERN_CLASS, theme.heroPatternClass)} />
        <div
          className={cx(
            "absolute top-20 right-10 w-72 h-72 blur-[100px] rounded-full",
            theme.heroTopOrbClass,
          )}
        />
        <div
          className={cx(
            "absolute bottom-10 left-10 w-72 h-72 blur-[100px] rounded-full",
            theme.heroBottomOrbClass,
          )}
        />

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div
              className={cx(
                "inline-flex items-center gap-2 px-5 py-2 rounded-full mb-8 animate-fade-in-up",
                theme.heroBadgeClass,
              )}
            >
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span
                className={cx(
                  "font-bold text-xs uppercase tracking-wider",
                  theme.heroBadgeTextClass,
                )}
              >
                {hero.badge}
              </span>
            </div>

            <h1
              className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-[1.1] animate-fade-in-up"
              style={{ animationDelay: "0.1s" }}
            >
              {hero.titlePrefix}{" "}
              <span className={theme.heroHighlightClass}>{hero.titleHighlight}</span>
            </h1>

            <p
              className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in-up"
              style={{ animationDelay: "0.2s" }}
            >
              {hero.descriptionPrefix}{" "}
              <span className={cx("font-bold", theme.heroSubtitleHighlightClass)}>
                {hero.descriptionHighlight}
              </span>
              {hero.descriptionSuffix ? ` ${hero.descriptionSuffix}` : null}
              {hero.supportingLine ? (
                <>
                  <br />
                  <span className="text-base md:text-lg opacity-80 mt-2 block italic">
                    {hero.supportingLine}
                  </span>
                </>
              ) : null}
            </p>

            <div
              className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 animate-fade-in-up w-full px-4 sm:px-0"
              style={{ animationDelay: "0.3s" }}
            >
              <button onClick={() => openQuiz("hero_primary")} className={theme.heroPrimaryButtonClass}>
                <IoCalendarOutline size={22} />
                {hero.primaryCtaText}
              </button>
              <a href="#experts" className={theme.heroSecondaryButtonClass}>
                {hero.secondaryCtaText} <IoArrowForward size={16} />
              </a>
            </div>

            <div
              className="flex flex-wrap items-center justify-center gap-8 md:gap-16 animate-fade-in-up"
              style={{ animationDelay: "0.4s" }}
            >
              {hero.stats.map((stat) => (
                <div key={stat.label} className="flex items-center gap-3">
                  <div
                    className={cx(
                      "w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-sm",
                      stat.iconShellClass,
                    )}
                  >
                    <span className={stat.iconClass}>{stat.icon}</span>
                  </div>
                  <div className="text-left">
                    <p className="text-white font-bold text-sm">{stat.value}</p>
                    <p className="text-gray-400 text-[11px]">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 hidden md:block">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full">
            <path
              d="M0 60V30C240 0 480 0 720 30C960 60 1200 60 1440 30V60H0Z"
              fill={theme.heroBottomCurveFill}
            />
          </svg>
        </div>
      </section>

      <section className="py-16 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10 md:mb-16">
            <div
              className={cx(
                "inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4",
                theme.benefitsBadgeClass,
              )}
            >
              <IoCheckmarkCircleOutline size={16} />
              <span
                className={cx(
                  "font-bold text-xs uppercase tracking-wider",
                  theme.benefitsBadgeTextClass,
                )}
              >
                {benefits.tag}
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-accent mb-4">
              {benefits.title}
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              {benefits.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {benefitItems.map((item, index) => (
              <ServiceCard key={`${item.label}-${index}`} {...item} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-28 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 md:mb-20">
            <div
              className={cx(
                "inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4",
                theme.processBadgeClass,
              )}
            >
              <IoCalendarOutline size={16} />
              <span
                className={cx(
                  "font-bold text-xs uppercase tracking-wider",
                  theme.processBadgeTextClass,
                )}
              >
                {process.tag}
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-accent mb-4">
              {process.title}
            </h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">
              {process.description}
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto mb-16">
            <div
              className={cx(
                "hidden md:block absolute top-12 left-[16%] right-[16%] h-[2px]",
                theme.stepConnectorClass,
              )}
            >
              <div className={cx("absolute inset-0", theme.stepConnectorDashClass)} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
              {process.items.map((item) => (
                <StepCard key={item.number} {...item} theme={theme} />
              ))}
            </div>
          </div>

          <div className="mt-12 md:mt-0 flex justify-center">
            <button onClick={() => openQuiz("process_cta")} className={theme.heroPrimaryButtonClass}>
              <IoCalendarOutline size={22} />
              {process.ctaText}
            </button>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10 md:mb-16">
            <div
              className={cx(
                "inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4",
                theme.reasonsBadgeClass,
              )}
            >
              <IoStar size={16} />
              <span
                className={cx(
                  "font-bold text-xs uppercase tracking-wider",
                  theme.reasonsBadgeTextClass,
                )}
              >
                {reasons.tag}
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-accent mb-4">
              {reasons.title}
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              {reasons.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {reasons.items.map((item) => (
              <ProblemCard key={item.label} {...item} onOpenQuiz={() => openQuiz(`problem_${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`)} />
            ))}
          </div>
        </div>
      </section>

      <section id="experts" className={cx("py-16 md:py-28", theme.expertsSectionClass)}>
        <div className="container mx-auto px-4">
          <div className="mb-10 flex flex-col items-center gap-6 md:mb-16">
            <div className="mx-auto max-w-2xl text-center">
              <div
                className={cx(
                  "inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4",
                  theme.expertsBadgeClass,
                )}
              >
                <IoRibbonOutline size={16} />
                <span
                  className={cx(
                    "font-bold text-xs uppercase tracking-wider",
                    theme.expertsBadgeTextClass,
                  )}
                >
                  {expertsSection.tag}
                </span>
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-accent mb-5">
                {expertsSection.title}
              </h2>
              <p className="text-gray-500 text-lg max-w-2xl leading-relaxed">
                {expertsSection.description}
              </p>
            </div>
          </div>

          <div className="mx-auto max-w-5xl overflow-hidden">
            {expertsLoading ? (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {Array.from({ length: 2 }).map((_, index) => (
                  <div
                    key={`expert-skeleton-${index}`}
                    className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm"
                  >
                    <div className="flex items-center gap-6 mb-7">
                      <div className="w-28 h-28 rounded-xl bg-gray-100 animate-pulse" />
                      <div className="flex-1 space-y-3">
                        <div className="h-6 w-2/3 rounded-full bg-gray-100 animate-pulse" />
                        <div className="h-4 w-3/4 rounded-full bg-gray-100 animate-pulse" />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="h-4 w-full rounded-full bg-gray-100 animate-pulse" />
                      <div className="h-4 w-full rounded-full bg-gray-100 animate-pulse" />
                      <div className="h-4 w-4/5 rounded-full bg-gray-100 animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : experts.length > 0 ? (
              <Swiper
                modules={[Autoplay]}
                spaceBetween={24}
                loop={experts.length > 1}
                autoplay={{
                  delay: 4500,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }}
                onSwiper={(swiper) => {
                  expertCarouselRef.current = swiper;
                }}
                breakpoints={{
                  320: { slidesPerView: 1 },
                  768: { slidesPerView: 1.1 },
                  1024: { slidesPerView: 1.5 },
                  1280: { slidesPerView: 2 },
                }}
                className="!overflow-hidden"
              >
                {experts.map((expert, index) => (
                  <SwiperSlide key={expert.id} className="h-auto pb-2">
                    <ExpertPanelCard expert={expert} index={index} theme={theme} />
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              <div className="rounded-2xl border border-gray-100 bg-white p-8 text-center text-gray-500 shadow-sm">
                {expertsSection.emptyState}
              </div>
            )}
            <div className="flex items-center justify-center gap-3 md:justify-end">
              <button
                type="button"
                onClick={() => expertCarouselRef.current?.slidePrev()}
                className={cx(
                  "flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm transition",
                  theme.carouselButtonClass,
                )}
              >
                <IoArrowBack size={18} />
              </button>
              <button
                type="button"
                onClick={() => expertCarouselRef.current?.slideNext()}
                className={cx(
                  "flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm transition",
                  theme.carouselButtonClass,
                )}
              >
                <IoArrowForward size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-28 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10 md:mb-16">
            <div
              className={cx(
                "inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4",
                theme.testimonialsBadgeClass,
              )}
            >
              <IoStar size={16} />
              <span
                className={cx(
                  "font-bold text-xs uppercase tracking-wider",
                  theme.testimonialsBadgeTextClass,
                )}
              >
                {testimonials.tag}
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-accent mb-4">
              {testimonials.title}
            </h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">
              {testimonials.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16">
            {testimonials.items.map((item, index) => (
              <TestimonialCard
                key={`${item.name}-${index}`}
                {...item}
                index={index}
                theme={theme}
              />
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto">
            {testimonials.stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div
                  className={cx(
                    "text-3xl md:text-4xl font-bold mb-1 font-serif",
                    theme.testimonialStatValueClass,
                  )}
                >
                  {stat.value}
                </div>
                <p className="text-gray-500 text-xs">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {supplementaryContent}

      <section className={cx("py-16 md:py-28", theme.finalSectionClass)}>
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-start max-w-6xl mx-auto">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-accent mb-4 text-center lg:text-left">
                {whyChoose.title}
              </h2>
              <p className="text-gray-500 leading-relaxed mb-10 text-center lg:text-left">
                {whyChoose.description}
              </p>

              <div className="space-y-5">
                {whyChoose.items.map((item) => (
                  <div
                    key={item.title}
                    className="flex items-start gap-4 p-5 bg-white border border-gray-100 rounded-2xl hover:shadow-md transition-shadow"
                  >
                    <div
                      className={cx(
                        "flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center text-xl",
                        theme.trustCardIconShellClass,
                        theme.trustCardIconClass,
                      )}
                    >
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-accent text-sm mb-1">{item.title}</h4>
                      <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div ref={formRef} id="booking-section">
              <BookingCTA config={bookingCta} theme={theme} onOpenQuiz={() => openQuiz("booking_cta")} />
            </div>
          </div>
        </div>
      </section>

      <div
        className={cx(
          "fixed bottom-0 left-0 right-0 p-3 z-50 pb-safe transition-all duration-300",
          theme.stickyBarClass,
          isFormVisible
            ? "translate-y-full opacity-0 pointer-events-none"
            : "translate-y-0 opacity-100",
        )}
      >
        <div className="container mx-auto flex items-center justify-between gap-4">
          <div className="hidden md:block">
            <p className="font-bold text-accent text-sm leading-tight">{stickyCta.title}</p>
            <p className="text-[11px] text-gray-400">{stickyCta.subtitle}</p>
          </div>
          <button onClick={() => openQuiz("sticky_cta")} className={theme.stickyButtonClass}>
            <IoCalendarOutline size={18} />
            {stickyCta.buttonText}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fade-in-up { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in-up { animation: fade-in-up 0.7s ease-out forwards; opacity: 0; }
      `}</style>
    </div>
  );
};

export default ConsultationLandingClient;
